import { useState } from 'react';

interface CalculatorCTAProps {
  formUrl: string;
}

export function CalculatorCTA({ formUrl }: CalculatorCTAProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name) return;
    setIsSubmitting(true);

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const functionUrl = `${supabaseUrl}/functions/v1/send-revenue-lead-email`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name,
          email: email,
          propertyAddress: address,
          source: 'Revenue Calculator'
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        setSubmitted(true);
      } else {
        console.error('Submission failed:', result.error);
        // Still show success to user
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
      // Still show success to user
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 lg:py-20 bg-gradient-to-br from-[#F0F0F0] via-white to-[#F0F0F0]">
      <div className="max-w-4xl mx-auto px-6">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="grid md:grid-cols-2">
            {/* Left: Image */}
            <div className="relative h-64 md:h-auto">
              <img
                src="https://public.readdy.ai/ai/img_res/973fe7875ea09fd0149cefb79d72998d.jpg"
                alt="Beautifully furnished rental property interior"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent md:bg-gradient-to-r md:from-transparent md:to-transparent"></div>
            </div>

            {/* Right: Form */}
            <div className="p-8 lg:p-10">
              {!submitted ? (
                <>
                  <h2 className="font-['Fraunces'] text-2xl lg:text-3xl font-bold text-[#505A5A] mb-3">
                    Want a personalized revenue report?
                  </h2>
                  <p className="text-sm text-[#505A5A]/70 mb-6 leading-relaxed">
                    Get a detailed, property-specific analysis from our team — including compliance check, market comparison, and revenue optimization tips.
                  </p>

                  <form
                    id="revenue-lead-form"
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div>
                      <input
                        type="text"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        placeholder="Your name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#882629] focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        placeholder="Your email"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#882629] focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        name="property_address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Property address (optional)"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#882629] focus:border-transparent text-sm"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#882629] text-white px-6 py-3.5 rounded-full text-sm font-semibold hover:bg-[#6E1F22] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 shadow-lg"
                    >
                      {isSubmitting ? 'Sending...' : 'Get My Free Report'}
                    </button>
                  </form>

                  <p className="text-[10px] text-[#505A5A]/50 mt-3 text-center">
                    No spam. We&apos;ll only contact you about your property assessment.
                  </p>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-8">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-4">
                    <i className="ri-check-line text-green-600 text-3xl"></i>
                  </div>
                  <h3 className="font-['Fraunces'] text-2xl font-bold text-[#505A5A] mb-2">
                    Request received!
                  </h3>
                  <p className="text-sm text-[#505A5A]/70 max-w-xs">
                    Our team will review your property details and send you a personalized revenue report within 24 hours.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 gap-4 mt-8">
          <a
            href="/property-assessment"
            className="flex items-center gap-4 bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-[#882629]/10 rounded-xl flex-shrink-0 group-hover:bg-[#882629] transition-colors">
              <i className="ri-file-list-3-line text-[#882629] text-xl group-hover:text-white transition-colors"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#505A5A]">Full Property Assessment</p>
              <p className="text-xs text-[#505A5A]/60">Detailed compliance &amp; eligibility check</p>
            </div>
            <i className="ri-arrow-right-s-line text-[#505A5A]/40 ml-auto text-lg"></i>
          </a>
          <a
            href="/book-call"
            className="flex items-center gap-4 bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-[#882629]/10 rounded-xl flex-shrink-0 group-hover:bg-[#882629] transition-colors">
              <i className="ri-phone-line text-[#882629] text-xl group-hover:text-white transition-colors"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#505A5A]">Book a Free Call</p>
              <p className="text-xs text-[#505A5A]/60">Speak with a rental strategy expert</p>
            </div>
            <i className="ri-arrow-right-s-line text-[#505A5A]/40 ml-auto text-lg"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
